/**
 * Mapas de densidad espacial de muestras de mirada estimadas.
 *
 * Algoritmo (versión en `ALGORITHM_VERSION`):
 * 1. Rejilla canónica con lado largo `gridLongSide` y el aspecto de la imagen original.
 * 2. Histograma de muestras válidas sobre la imagen (coordenadas normalizadas).
 * 3. Suavizado gaussiano separable con sigma = `sigmaFraction * gridLongSide` celdas; fuera de la
 *    rejilla se asume cero (la masa que sale se descarta antes de renormalizar).
 * 4. Normalización a suma 1 por participante.
 * 5. Promedio simple de matrices normalizadas por participante (cada persona pesa igual).
 */

export interface Grid {
  width: number;
  height: number;
  values: Float64Array;
}

export function gridDimensions(naturalWidth: number, naturalHeight: number, longSide: number): { width: number; height: number } {
  if (!(naturalWidth > 0 && naturalHeight > 0)) throw new Error("Dimensiones de imagen inválidas");
  if (naturalWidth >= naturalHeight) {
    return { width: longSide, height: Math.max(1, Math.round((longSide * naturalHeight) / naturalWidth)) };
  }
  return { width: Math.max(1, Math.round((longSide * naturalWidth) / naturalHeight)), height: longSide };
}

export function createGrid(width: number, height: number): Grid {
  return { width, height, values: new Float64Array(width * height) };
}

/** Índice de celda para una coordenada normalizada; el borde 1 cae en la última celda. */
export function cellIndex(norm: number, size: number): number {
  return Math.min(size - 1, Math.max(0, Math.floor(norm * size)));
}

/** Histograma de puntos normalizados. Ignora (no recorta) puntos fuera de [0,1]². */
export function histogram(points: ReadonlyArray<{ x: number; y: number }>, width: number, height: number): Grid {
  const g = createGrid(width, height);
  for (const p of points) {
    if (!(p.x >= 0 && p.x <= 1 && p.y >= 0 && p.y <= 1)) continue;
    const col = cellIndex(p.x, width);
    const row = cellIndex(p.y, height);
    g.values[row * width + col]! += 1;
  }
  return g;
}

export function gaussianKernel(sigma: number): Float64Array {
  if (!(sigma > 0)) return Float64Array.of(1);
  const radius = Math.max(1, Math.ceil(sigma * 3));
  const k = new Float64Array(radius * 2 + 1);
  let sum = 0;
  for (let i = -radius; i <= radius; i++) {
    const v = Math.exp(-(i * i) / (2 * sigma * sigma));
    k[i + radius] = v;
    sum += v;
  }
  for (let i = 0; i < k.length; i++) k[i]! /= sum;
  return k;
}

/** Desenfoque gaussiano separable con relleno de ceros. */
export function gaussianBlur(grid: Grid, sigmaCells: number): Grid {
  const { width, height } = grid;
  const kernel = gaussianKernel(sigmaCells);
  const r = (kernel.length - 1) / 2;
  const tmp = new Float64Array(width * height);
  const out = new Float64Array(width * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let acc = 0;
      for (let k = -r; k <= r; k++) {
        const xx = x + k;
        if (xx < 0 || xx >= width) continue;
        acc += grid.values[y * width + xx]! * kernel[k + r]!;
      }
      tmp[y * width + x] = acc;
    }
  }
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let acc = 0;
      for (let k = -r; k <= r; k++) {
        const yy = y + k;
        if (yy < 0 || yy >= height) continue;
        acc += tmp[yy * width + x]! * kernel[k + r]!;
      }
      out[y * width + x] = acc;
    }
  }
  return { width, height, values: out };
}

export function gridSum(grid: Grid): number {
  let s = 0;
  for (const v of grid.values) s += v;
  return s;
}

export function gridMax(grid: Grid): number {
  let m = 0;
  for (const v of grid.values) if (v > m) m = v;
  return m;
}

export function gridMaxAbs(grid: Grid): number {
  let m = 0;
  for (const v of grid.values) if (Math.abs(v) > m) m = Math.abs(v);
  return m;
}

/** Normaliza a suma 1. Devuelve `null` si la suma es 0 (sin muestras): nunca se fabrica densidad. */
export function normalizeSum(grid: Grid): Grid | null {
  const s = gridSum(grid);
  if (!(s > 0)) return null;
  const values = new Float64Array(grid.values.length);
  for (let i = 0; i < values.length; i++) values[i] = grid.values[i]! / s;
  return { width: grid.width, height: grid.height, values };
}

/** Matriz individual: histograma + suavizado + normalización (suma 1). */
export function participantDensity(
  points: ReadonlyArray<{ x: number; y: number }>,
  width: number,
  height: number,
  sigmaFraction: number,
): Grid | null {
  const h = histogram(points, width, height);
  const blurred = gaussianBlur(h, sigmaFraction * Math.max(width, height));
  return normalizeSum(blurred);
}

/** Promedio simple de matrices (misma dimensión). */
export function meanGrids(grids: ReadonlyArray<Grid>): Grid | null {
  if (grids.length === 0) return null;
  const { width, height } = grids[0]!;
  const out = createGrid(width, height);
  for (const g of grids) {
    if (g.width !== width || g.height !== height) throw new Error("Las matrices deben tener la misma dimensión");
    for (let i = 0; i < out.values.length; i++) out.values[i]! += g.values[i]!;
  }
  for (let i = 0; i < out.values.length; i++) out.values[i]! /= grids.length;
  return out;
}

/** Diferencia firmada `a - b`. */
export function diffGrids(a: Grid, b: Grid): Grid {
  if (a.width !== b.width || a.height !== b.height) throw new Error("Las matrices deben tener la misma dimensión");
  const out = createGrid(a.width, a.height);
  for (let i = 0; i < out.values.length; i++) out.values[i] = a.values[i]! - b.values[i]!;
  return out;
}

/** Fracción de celdas de una rejilla gruesa `n×n` con al menos un punto en imagen. */
export function coverage(points: ReadonlyArray<{ x: number; y: number }>, n: number): number | null {
  if (points.length === 0) return null;
  const h = histogram(points, n, n);
  let visited = 0;
  for (const v of h.values) if (v > 0) visited++;
  return visited / (n * n);
}

/** Serializa una rejilla para JSON con precisión suficiente (no se redondean coordenadas, solo densidades). */
export function gridToDto(grid: Grid, signed = false): { width: number; height: number; values: number[]; max: number } {
  return {
    width: grid.width,
    height: grid.height,
    values: Array.from(grid.values, (v) => Number(v.toPrecision(6))),
    max: signed ? gridMaxAbs(grid) : gridMax(grid),
  };
}
